import { Vms } from '_lib/api/types';
import { BlockedActionMode } from '_lib/auth/rbac';
import { RootState, useAppDispatch } from '_store';
import applicationReducer from '_store/application/reducer';
import { DialogType } from '_store/application/types';
import { vmsEnrichmentName } from '_store/pmv/pmvsTypes';
import { isVmsWorking, pmvStatusIcon } from '_store/pmv/utils';
import { useLocalization } from '@fluent/react';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, CardContent, Tooltip, Typography } from '@material-ui/core';
import { blue, red } from '@material-ui/core/colors';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { Accordion } from '../../../items/Accordion';
import { RoleGuard } from '../../../items/RoleGuard';
import { toLatLng } from '../../../routes/MapView/utils';
import PmvCardDetails from './Details';
import styles from './styles';

export interface PmvCardListItemProps {
  vms: Vms;
  asCard: boolean;
  onUpdate?: (idExpanded: string) => void;
  onDelete?: (id: string) => void;
  writeAllowed?: boolean;
  blockedActionMode?: BlockedActionMode;
  blockedMessage?: string;
}

const PmvCardListItem: FC<PmvCardListItemProps> = ({
  vms,
  onUpdate,
  onDelete,
  asCard,
  writeAllowed = true,
  blockedActionMode = 'disable',
  blockedMessage = '',
}) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const mapRef = useSelector((state: RootState) => state.application.mapRef);
  const dialog = useSelector((state: RootState) => state.application.activeDialog);

  const displayName = vmsEnrichmentName(vms) || vms.id;
  const expandDetailsByDefault =
    dialog?.type === DialogType.PMV_PATH && dialog.id === vms.id;

  const onAccordionExpanded = () => {
    if (!vms.latitude || !vms.longitude || !mapRef) return;

    const markerPosition = toLatLng([vms.longitude, vms.latitude]);
    mapRef.flyTo(markerPosition, 15, { animate: true, duration: 1 });

    setTimeout(() => {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.PMV_PATH,
          id: vms.id,
        }),
      );
    }, 1000);
  };

  const onAccordionClose = () => {
    dispatch(
      applicationReducer.actions.infoSelected({
        type: DialogType.PMV_LIST,
        id: 'pmv-list',
      }),
    );
  };

  const details = <PmvCardDetails vms={vms} />;

  const body = (
    <CardContent className={classes.cardContent}>
      <Box className={classes.headerBox}>
        <div className={classes.headerIconAndTitle}>
          <img
            src={pmvStatusIcon(isVmsWorking(vms))}
            alt=""
            width={24}
            height={24}
            style={{ flexShrink: 0 }}
          />
          <span>{displayName}</span>
        </div>
        <div>
          <RoleGuard
            allowed={writeAllowed}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <Tooltip title={l10n.getString('forms-event-update-btn')}>
              <FontAwesomeIcon
                icon={faPen}
                style={{ backgroundColor: blue[400] }}
                className={classes.iconButton}
                size="xs"
                onClick={() => onUpdate?.(vms.id)}
              />
            </Tooltip>
          </RoleGuard>
          <RoleGuard
            allowed={writeAllowed}
            blockedActionMode={blockedActionMode}
            blockedMessage={blockedMessage}
          >
            <Tooltip title={l10n.getString('forms-event-delete-btn')}>
              <FontAwesomeIcon
                icon={faTrash}
                style={{ backgroundColor: red[400] }}
                className={classes.iconButton}
                size="xs"
                onClick={() => onDelete?.(vms.id)}
              />
            </Tooltip>
          </RoleGuard>
        </div>
      </Box>

      <div className={classes.summaryRow}>
        <div className={classes.summaryField}>
          <Typography variant="caption" color="textSecondary" display="block">
            {l10n.getString('forms-pmv-list-id')}
          </Typography>
          <Typography variant="body2" className={classes.summaryValue}>
            <strong>{vms.id}</strong>
          </Typography>
        </div>
        <div className={classes.summaryField}>
          <Typography variant="caption" color="textSecondary" display="block">
            {l10n.getString('forms-pmv-detail-description')}
          </Typography>
          <Typography variant="body2" className={classes.summaryValue}>
            <strong>{vms.description || '-'}</strong>
          </Typography>
        </div>
      </div>

      {asCard ? (
        <Accordion
          title={l10n.getString('forms-pmv-view-all-details')}
          onExpanded={onAccordionExpanded}
          onClose={onAccordionClose}
          isExpandedByDefault={expandDetailsByDefault}
        >
          {details}
        </Accordion>
      ) : (
        details
      )}
    </CardContent>
  );

  if (asCard) {
    return <Card className={classes.root}>{body}</Card>;
  }
  return body;
};

export default React.memo(PmvCardListItem);
